import type { VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'
import { FOCUS_RING } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'

const buttonVariants = cva(
  `rounded-control inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-150 active:scale-[0.97] ${FOCUS_RING} disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
  {
    variants: {
      variant: {
        default:
          'bg-[hsl(var(--button-primary))] text-[hsl(var(--button-primary-foreground))] shadow-sm hover:bg-[hsl(var(--button-primary))]/90 active:bg-[hsl(var(--button-primary))]/95',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 active:bg-destructive/95',
        outline:
          'border border-slate-200 bg-background hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:bg-slate-800/50',
        secondary:
          'bg-slate-100 text-slate-900 hover:bg-slate-200/80 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700/80',
        ghost:
          'hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800/50 dark:hover:text-slate-100',
        link: 'text-primary underline-offset-4 hover:underline active:scale-100',
        gradient:
          'bg-gradient-to-r from-[hsl(var(--button-primary))] to-[hsl(var(--button-primary))]/80 text-[hsl(var(--button-primary-foreground))] shadow-sm hover:opacity-90',
        glass:
          'border border-slate-200/50 bg-white shadow-sm hover:bg-slate-50 dark:border-slate-700/50 dark:bg-slate-900 dark:hover:bg-slate-800',
      },
      size: {
        // macOS 26 control scale: 32px regular, 28px small, 40px large.
        default: 'h-8 px-3.5 py-1.5',
        sm: 'h-7 px-2.5 text-xs',
        lg: 'h-10 px-5 text-base',
        xl: 'h-12 px-8 text-base',
        icon: 'h-8 w-8',
      },
      animation: {
        none: '',
        subtle: '',
        bounce: 'hover:scale-[1.02] active:scale-[0.98]',
        glow: 'hover:shadow-md hover:shadow-primary/20',
        magnetic: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      animation: 'none',
    },
  },
)

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  loading?: boolean
  animation?: 'none' | 'subtle' | 'bounce' | 'glow'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      animation,
      asChild = false,
      icon,
      iconPosition = 'left',
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'

    // When asChild is true, Slot expects exactly one child element.
    // Extra null siblings from conditional expressions break React.Children.count.
    if (asChild) {
      return (
        <Comp
          className={cn(
            buttonVariants({ variant, size, animation, className }),
          )}
          ref={ref}
          disabled={disabled || loading}
          {...props}
        >
          {children}
        </Comp>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, animation, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : icon && iconPosition === 'left' ? (
          <span className="mr-2">{icon}</span>
        ) : null}

        {children}

        {icon && iconPosition === 'right' && !loading ? (
          <span className="ml-2">{icon}</span>
        ) : null}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
